// src/services/authService.js
import { api } from './interceptors';

/**
 * Authentication Service - Handles authentication related API calls
 */
const authService = {
  /**
   * Get JWT token using Auth Code
   * @param {string} provider - SSO provider (e.g., 'google', 'facebook', 'github')
   * @param {string} authCode - Provider's ID token
   * @returns {Promise} - Returns JWT token and user data
   */
  getJWTToken: async (provider, authCode) => {
    try {
      const response = await api.post(`/jwt/${provider}/sso-login/`, {
        auth_code: authCode
      });

      // Store the token in localStorage
      if (response.data?.access_token) {
        localStorage.setItem('authToken', response.data.access_token);
      }

      return response.data;
    } catch (error) {
      // Clear token on error
      localStorage.removeItem('authToken');
      throw error;
    }
  },

  /**
   * Get JWT token using Google SSO (for backward compatibility)
   * @param {string} authCode - Google ID token
   * @returns {Promise} - Returns JWT token and user data
   */
  getJWTTokenGoogle: async (authCode) => {
    return authService.getJWTToken('google', authCode);
  },

  /**
   * Logout user
   * @returns {void}
   */
  signOut: () => {
    // todo: Call backend to logout
    localStorage.removeItem('authToken');
    window.location.href = '/';
  },

  storeToken: (token) => {
    localStorage.setItem('authToken', token);
  },

  /**
   * Check if user is authenticated
   * @returns {boolean}
   */
  isAuthenticated: () => {
    return !localStorage.getItem('authToken');
  },

  /**
   * Get stored auth token
   * @returns {string|null}
   */
  getToken: () => {
    return localStorage.getItem('authToken');
  },

  /**
   * Request magic login token
   * @param {string} email - Email address to send magic login token to
   * @returns {Promise} - Returns success message
   */
  requestMagicLogin: async (email) => {
    try {
      const response = await api.post('/jwt/magic-link/request/', {
        email
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  /**
   * Verify magic login token
   * @param {string} token - Magic login token from URL
   * @returns {Promise} - Returns JWT token and user data
   */
  verifyMagicLoginToken: async (token) => {
    try {
      const response = await api.post('/jwt/magic-link/verify/', {
        token
      });

      // Store the token in localStorage
      if (response.data?.access_token) {
        localStorage.setItem('authToken', response.data.access_token);
      }

      return response.data;
    } catch (error) {
      // Clear token on error
      localStorage.removeItem('authToken');
      throw error;
    }
  },

  /**
   * Login with email and password
   * @param {string} email - User email
   * @param {string} password - User password
   * @returns {Promise} - Returns JWT token and user data
   */
  login: async (email, password) => {
    try {
      const response = await api.post('/jwt/token/', {
        email,
        password
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  /**
   * Sign up with email and password, and name
   * @param {string} email - User email
   * @param {string} first_name - User first name
   * @param {string} last_name - User last name
   * @param {string} phone - User last name
   * @param {string} password - User password
   * @returns {Promise} - Returns success message
   */
  signup: async (email, first_name, last_name, phone, password) => {
    try {
      const response = await api.post('/jwt/signup/', {
        email,
        first_name, 
        last_name,
        phone,
        password
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  /**
   * Request password reset
   * @param {string} email - Email address to send reset password link to
   * @returns {Promise} - Returns success message
   */
  requestPasswordReset: async (email) => {
    try {
      const response = await api.post('/users/reset-password-request/', {
        email
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  /**
   * Confirm password reset
   * @param {string} uid - User ID from reset link
   * @param {string} token - Reset token from reset link
   * @param {string} newPassword - New password
   * @param {string} confirmPassword - Confirm new password
   * @returns {Promise} - Returns success message
   */
  confirmPasswordReset: async (uid, token, newPassword, confirmPassword) => {
    try {
      const response = await api.post('/users/confirm-reset-password/', {
        uid,
        token,
        new: newPassword,
        confirm: confirmPassword
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  }
};

export default authService;